import { useCallback, useMemo, useState } from 'react';

/**
 * The "@" typing behaviour, without any chrome.
 *
 * MentionTextarea (signals, club forum) draws a bordered field with a highlight
 * mirror and drops its suggestions below. The message composer can't use any of
 * that — it sits at the bottom of the screen, has its own auto-grow sizing and an
 * inline emoji button, and its list has to open upward. Only the chrome differs,
 * so only the chrome is duplicated: detecting the token being typed and replacing
 * it on pick live here, once.
 *
 * Usage: feed it every keystroke and caret move via `sync`, read `matches` to
 * render your own list, and call `insert(person)` when one is chosen.
 */

/** What's being typed right before the caret: "@" then handle characters. */
const TYPING_RE = /(?:^|\s)@([a-zA-Z0-9_]*)$/;

export default function useMentionAutocomplete({ value, onChange, people = [], maxLength = 4000, onTag }) {
  // { query, start } while a mention is being typed, else null.
  const [mention, setMention] = useState(null);

  const sync = useCallback((text, caret) => {
    const before = (text || '').slice(0, caret);
    const m = before.match(TYPING_RE);
    setMention(m ? { query: m[1], start: caret - m[1].length - 1 } : null);
  }, []);

  const close = useCallback(() => setMention(null), []);

  const matches = useMemo(() => {
    if (mention == null) return [];
    const q = mention.query.toLowerCase();
    return people
      .filter((p) => !q
        || (p.handle || '').toLowerCase().includes(q)
        || (p.displayName || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, people]);

  /**
   * Replace the half-typed token with the chosen person and report the caret
   * position the field should restore. Tags with @handle when there is one; a
   * handle-less person is tagged by first name, and `onTag` carries their id so
   * the server can still resolve who was meant.
   */
  const insert = useCallback((person) => {
    if (!mention) return null;
    const firstName = (person.displayName || '').trim().split(/\s+/)[0].replace(/[^a-zA-Z0-9_]/g, '');
    const slug = person.handle || firstName || 'member';
    const before = (value || '').slice(0, mention.start);
    const after = (value || '').slice(mention.start + 1 + mention.query.length);
    const next = `${before}@${slug} ${after}`.slice(0, maxLength);
    onChange(next);
    onTag?.({ token: slug.toLowerCase(), userId: person.userId });
    setMention(null);
    return { caret: before.length + slug.length + 2 };
  }, [mention, value, onChange, maxLength, onTag]);

  return { mention, matches, sync, insert, close, isOpen: mention != null && matches.length > 0 };
}
